/**
 * Global `fetch` shim — installed by `SDKProvider` when `apiUrl` or
 * `apiHeaders` is set.
 *
 * Why this exists: the codegen-emitted spec `queryFn`s call bare
 * `await fetch('/v1/markets', ...)` with absolute-path URLs. On Next.js
 * the dev proxy rewrites `/v1/*` → backend. On React Native there is
 * no proxy — the bundle ships unmodified and `fetch('/v1/markets')`
 * resolves to nothing. Same hazard applies to any web build deployed
 * behind a CDN that doesn't path-rewrite to the API.
 *
 * The shim wraps `globalThis.fetch` once at provider mount and:
 *   1. Rewrites relative `/v1/*` paths against the configured `apiUrl`.
 *   2. Injects `apiHeaders` (e.g. `X-API-Key`) onto Morfi-API-bound
 *      requests ONLY — `/v1/*` paths and absolute URLs on the `apiUrl`
 *      origin. Third-party origins never receive them.
 *   3. Leaves absolute URLs unchanged so consumers can still hit
 *      third-party APIs (e.g. avatars, public CDN) without rewriting.
 *
 * Idempotent: install short-circuits if the shim is already active for
 * the same configuration. Returns an uninstall function for cleanup
 * (used by `SDKProvider` on unmount).
 *
 * Targeted to `globalThis.fetch` rather than the platform adapter so it
 * works uniformly across (a) the codegen-emitted spec queryFns, (b)
 * mutation handlers (`createMutation`'s `await fetch(url, init)`), and
 * (c) any consumer-side `fetch` calls that hit `/v1/*` — three different
 * paths that would otherwise each need their own wiring.
 */

export interface FetchShimConfig {
  /**
   * Absolute base URL. Relative `/v1/*` paths are rewritten as
   * `${apiUrl}${path}`. When omitted (and headers are also omitted),
   * the shim is a no-op installed.
   */
  readonly apiUrl?: string;
  /**
   * Headers injected onto outgoing requests bound for the Morfi API
   * (`/v1/*` paths and absolute URLs on the `apiUrl` origin) — never
   * onto third-party origins. Typically `{ 'X-API-Key': '...' }`.
   * Existing headers on the call site take precedence — the shim merges
   * these in only when the call site hasn't already set them.
   */
  readonly apiHeaders?: Readonly<Record<string, string>>;
}

const SHIM_MARKER = Symbol.for('@morfi/backend-sdk-v2/fetchShim');

type MarkedFetch = typeof fetch & {
  [SHIM_MARKER]?: { readonly apiUrl: string; readonly headerHash: string };
};

/**
 * Mirror of the most-recently-installed `apiUrl`, exposed for codegen-emitted
 * synchronous URL builders (e.g. `getImageProxy`) that need to produce an
 * absolute URL for `<img src>` / `<a href>` consumers. Updated by
 * `installFetchShim` and read by `getInstalledApiUrl`. Empty string before any
 * provider mounts.
 */
let _installedApiUrl = '';

/**
 * Returns the apiUrl currently installed by `<SDKProvider>` — used by
 * codegen-emitted URL builders to compose absolute URLs for non-`fetch`
 * consumers (`<img src>`, `<a href>`, download). Returns the empty string
 * before any provider mounts; callers should treat that as "not ready" and
 * either gate rendering or fall back to a relative path.
 */
export function getInstalledApiUrl(): string {
  return _installedApiUrl;
}

function hashHeaders(headers?: Readonly<Record<string, string>>): string {
  if (headers === undefined) return '';
  const entries = Object.entries(headers).sort(([a], [b]) => a.localeCompare(b));
  return entries.map(([k, v]) => `${k}:${v}`).join('|');
}

/**
 * Install the shim. Returns an uninstall function. Calling twice with
 * the same config returns a no-op uninstall (shim is already correct).
 * Calling twice with DIFFERENT config replaces the shim — useful in dev
 * with HMR.
 */
export function installFetchShim(config: FetchShimConfig): () => void {
  const apiUrl = config.apiUrl ?? '';
  const apiHeaders = config.apiHeaders;
  if (apiUrl === '' && (apiHeaders === undefined || Object.keys(apiHeaders).length === 0)) {
    // Nothing to do — leave native fetch alone.
    return (): void => {
      /* no-op */
    };
  }

  const g = globalThis as unknown as { fetch: MarkedFetch };
  const previous = g.fetch;
  const headerHash = hashHeaders(apiHeaders);

  // Already shimmed with identical config — short-circuit.
  if (
    previous[SHIM_MARKER]?.apiUrl === apiUrl &&
    previous[SHIM_MARKER]?.headerHash === headerHash
  ) {
    return (): void => {
      /* no-op — caller didn't install us */
    };
  }

  // If an OLDER shim is active (different config), unwrap to its inner
  // fetch before installing the new one. Avoids stacking shims under HMR.
  let inner: typeof fetch = previous;
  while ((inner as MarkedFetch)[SHIM_MARKER] !== undefined) {
    const unwrap = (inner as unknown as { __morfi_inner?: typeof fetch }).__morfi_inner;
    if (unwrap === undefined) break;
    inner = unwrap;
  }

  const wrapped: MarkedFetch = ((
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const url =
      typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;

    // A request is Morfi-API-bound if it's a relative `/v1/*` or `/ai/*`
    // path (which we rewrite) or an absolute URL already on the `apiUrl`
    // origin. `apiHeaders` are injected ONLY for those — injecting the API
    // key onto a third-party origin (e.g. Turnkey's auth proxy) leaks the
    // credential AND trips that origin's CORS preflight, breaking it.
    // The `/ai/*` namespace covers AI proposal endpoints (`/ai/proposal/*`)
    // which today live outside the `/v1/*` prefix; treat them as first-class
    // Morfi-API routes.
    let nextUrl: RequestInfo | URL = input;
    let isApiBound = false;
    if (typeof url === 'string') {
      if (url.startsWith('/v1/') || url.startsWith('/ai/')) {
        isApiBound = true;
        if (apiUrl !== '') nextUrl = `${apiUrl}${url}`;
      } else if (apiUrl !== '' && url.startsWith(apiUrl)) {
        isApiBound = true;
      }
    }

    // Header merge — call site wins on conflict so consumers can
    // still override the API key per-request if needed.
    let nextInit: RequestInit | undefined = init;
    if (isApiBound && apiHeaders !== undefined && Object.keys(apiHeaders).length > 0) {
      const merged = new Headers(init?.headers ?? {});
      for (const [k, v] of Object.entries(apiHeaders)) {
        if (!merged.has(k)) merged.set(k, v);
      }
      nextInit = { ...(init ?? {}), headers: merged };
    }

    return inner(nextUrl, nextInit);
  }) as MarkedFetch;

  wrapped[SHIM_MARKER] = { apiUrl, headerHash };
  (wrapped as unknown as { __morfi_inner: typeof fetch }).__morfi_inner = inner;
  g.fetch = wrapped;
  _installedApiUrl = apiUrl;

  return (): void => {
    // Restore native fetch — but only if we're still the active shim.
    // Another caller may have installed a different shim on top of us
    // (HMR, multiple providers). In that case, leave them alone.
    if (g.fetch === wrapped) {
      g.fetch = inner;
      _installedApiUrl = '';
    }
  };
}
